import { useState, useMemo, useCallback, useEffect, useReducer } from "react";
import PropTypes from "prop-types";
import {
  Player,
  Video,
  VideoRecs,
  VideoFooter,
  MediaCard,
} from "@gonoodle/gn-universe-ui";
import { generatePath } from "@gonoodle/gn-universe-utils";
import { useRouter } from "next/router";
import { filter, find, random, compact } from "lodash";
import {
  QueryClient,
  useQuery,
  useMutation,
  useQueryClient,
} from "react-query";
import { isBefore, subWeeks } from "date-fns";
import { dehydrate } from "react-query/hydration";

import api from "../../api";
import { useImpression, sendImpression } from "../../utils/hooks";
import { useUser } from "../../contexts/user";
import UserSelfIdModal from "../../components/UserSelfIdModal";
import VideoTile from "../../components/VideoTile";
import Layout from "../../components/Layout";
import { useVideoSession, useRotatedAdProductOfType } from "../../hooks";
import {
  AD_PRODUCTS_TYPES,
  IMPRESSION_TYPES,
  QUERY_KEYS,
  USER_TYPES,
  ROUTE_PREFIX,
  IMPRESSION_PLACEMENT_TYPES,
  SPONSORED_PARTNER_TYPES,
  SECTIONS,
  SECTIONS_TYPES,
  PAGES_TYPES,
  CONTENT_TYPES,
  ROUTE_PATHS,
  VIDEO_PRE_ROLL_TYPES,
  ROUTE_PERMISSIONS,
} from "../../constants";
import { selectAdProduct, selectContentTag } from "../../selectors";
import { WithContentLink, withAuthorization } from "../../containers";
import ShareContentButton from "../../components/ShareContentButton";
import { useLogEvent } from "../../contexts/Analytics";
import config from "../../config";
import urls from "../../constants/urls";

const MediaCardWithLink = WithContentLink(MediaCard);

const getSponsoredIcon = (attribution) =>
  attribution?.type === "promotional_activity" ? attribution?.icon : null;
const getChannelIconFromTag = (tag) => tag?.images?.icon ?? null;
const getDefaultChannelIcon = (attribution) => attribution?.icon ?? null;

const getSponsoredPartner = ({ attribution, videoId }) =>
  [
    SPONSORED_PARTNER_TYPES.PROMOTIONAL_ACTIVITY,
    SPONSORED_PARTNER_TYPES.LONG_TERM_SPONSOR,
  ].includes(attribution?.type)
    ? {
        name: attribution?.name,
        logo: attribution?.logo,
        url:
          "https://support.gonoodle.com/article/22-the-gonoodle-sponsor-promise",
        impressionsProps: {
          video_version_id: videoId,
        },
      }
    : null;

const getContentHubPartner = (tag) => {
  const randNum = random(1, 100);
  const adProduct = selectAdProduct(tag?.adProductPartners, randNum);
  const logo = adProduct?.images?.videoPlayerLogo;
  return logo
    ? {
        name: adProduct?.partnerName,
        logo,
        url:
          "https://support.gonoodle.com/article/22-the-gonoodle-sponsor-promise",
        impressionsProps: {
          ad_product_type: adProduct?.type,
          partner_name: adProduct?.partnerName,
          partner_id: adProduct?.partnerId,
          ad_product_partner_id: adProduct?.id,
          content_hub_type: tag?.type,
          content_hub_name: tag?.name,
        },
      }
    : null;
};

const getAdProductPartner = ({ adProductVideoPlayerLogo, videoId }) => {
  const logo = adProductVideoPlayerLogo?.images?.videoPlayerLogo;
  return logo
    ? {
        name: adProductVideoPlayerLogo?.partnerName,
        logo,
        impressionsProps: {
          ad_product_type: adProductVideoPlayerLogo?.type,
          partner_name: adProductVideoPlayerLogo?.partnerName,
          partner_id: adProductVideoPlayerLogo?.partnerId,
          ad_product_partner_id: adProductVideoPlayerLogo?.id,
          video_version_id: videoId,
        },
      }
    : null;
};

const VideoPage = () => {
  const [isUserSelfIdModalOpen, setIsUserSelfIdModalOpen] = useState(false);
  const router = useRouter();
  const { identity, sourceId } = router.query;
  const [id] = identity;
  const client = api();
  const queryClient = useQueryClient();
  const { user } = useUser();

  const userTypesEligibleForAutoPlay = [
    USER_TYPES.UNREGISTERED_FAMILY.id,
    USER_TYPES.FAMILY.id,
  ];

  const { data: videoDetails } = useQuery([QUERY_KEYS.VIDEO_DETAILS, id], () =>
    client.getVideoDetails(id),
  );

  const {
    jwPlayerId,
    description,
    title,
    contentTags = [],
    printable,
    id: videoId,
    attribution,
    adTag,
  } = videoDetails;

  const randomContentTag = selectContentTag(videoDetails);

  const { data: contentTagVideos } = useQuery(
    [QUERY_KEYS.CONTENT_TAG_VIDEO_LIST, id],
    () => client.getContentTagVideos(randomContentTag.id),
    {
      enabled: !!randomContentTag,
    },
  );

  const recommendedVideos = useMemo(() => {
    if (Array.isArray(contentTagVideos)) {
      return filter(
        contentTagVideos,
        (recommendedVideo) => recommendedVideo.id !== videoDetails.id,
      );
    }
    return [];
  }, [contentTagVideos, videoDetails.id]);

  const { adProduct: adProductVideoPlayerLogo } = useRotatedAdProductOfType(
    AD_PRODUCTS_TYPES.VIDEO_PLAYER_LOGO,
  );

  const tag = useMemo(() => find(videoDetails.contentTags, { id: sourceId }), [
    videoDetails,
    sourceId,
  ]);

  const { data: adProductPreRollSlate } = useQuery(
    compact([QUERY_KEYS.AD_PRODUCT_PRE_ROLL_SLATE, videoId, sourceId]),
    () => client.getPreRollSlate(videoId, sourceId),
  );

  const calculatedAdTag = useMemo(() => {
    const userTypesEligibleForAds = [
      USER_TYPES.UNREGISTERED_FAMILY.id,
      USER_TYPES.FAMILY.id,
    ];

    const userTypesEligibleForPreRollSlate = [
      USER_TYPES.UNREGISTERED_TEACHER.id,
      USER_TYPES.CLASSROOM_TEACHER.id,
    ];

    const cutoff = subWeeks(new Date(), 1);
    const shouldSeeDisclaimer =
      !user.flags.lastDisclaimerSeenAt ||
      isBefore(new Date(user.flags.lastDisclaimerSeenAt), cutoff);

    const adInfo = {
      url: adTag,
      type: VIDEO_PRE_ROLL_TYPES.AD,
    };

    if (shouldSeeDisclaimer) {
      adInfo.url = `${urls.GN_ASSETS_BASE_URL}/pre-roll-bumpers/liability-disclaimer.xml`;
      adInfo.type = VIDEO_PRE_ROLL_TYPES.DISCLAIMER;
      return adInfo;
    }
    if (
      userTypesEligibleForPreRollSlate.includes(user.userTypeId) &&
      adProductPreRollSlate
    ) {
      adInfo.url = adProductPreRollSlate.adUrl;
      adInfo.type = VIDEO_PRE_ROLL_TYPES.SLATE;
      return adInfo;
    }
    if (!userTypesEligibleForAds.includes(user.userTypeId)) {
      return undefined;
    }
    if (!user.showAd && adTag) {
      adInfo.url = adTag.replace(/(request_type%3D)ad/, `$1blackboard`);
      adInfo.type = VIDEO_PRE_ROLL_TYPES.AD;
    }
    if (tag && adTag) {
      adInfo.url = adInfo.url.replace(/(content_hub%3D)none/, `$1${tag.slug}`);
      adInfo.type = VIDEO_PRE_ROLL_TYPES.AD;
    }
    return adInfo;
  }, [user, tag, adTag, adProductPreRollSlate]);

  const channelIcon = useMemo(
    () =>
      getSponsoredIcon(attribution) ??
      getChannelIconFromTag(tag) ??
      getDefaultChannelIcon(attribution),
    [tag, attribution],
  );

  const partner = useMemo(
    () =>
      getSponsoredPartner({ attribution, videoId }) ??
      getContentHubPartner(tag) ??
      getAdProductPartner({ adProductVideoPlayerLogo, videoId }),

    [attribution, videoId, tag, adProductVideoPlayerLogo],
  );

  const partnerLogoInViewRef = useImpression({
    properties: {
      impression_type: IMPRESSION_TYPES.LOGO,
      placement: IMPRESSION_PLACEMENT_TYPES.VIDEO_PLAYER,
      ...partner?.impressionsProps,
    },
    enabled: !!partner,
  });

  const onTagPress = (tagId, tagSlug) => {
    const path = generatePath(ROUTE_PATHS.TAGS, {
      id: tagId,
      slug: tagSlug,
    });
    router.push(path);
  };

  const { mutate: adViewed, isSuccess: adViewedSuccessfully } = useMutation(
    client.adViewed,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.USER);
        if (calculatedAdTag.type === VIDEO_PRE_ROLL_TYPES.SLATE) {
          sendImpression(
            {
              videoCompletionType: "pre_roll",
              adProductType: AD_PRODUCTS_TYPES.PRE_ROLL_SLATE,
              adProductPartnerId: adProductPreRollSlate.adProductPartnerId,
              partnerId: adProductPreRollSlate.partnerId,
              partnerName: adProductPreRollSlate.partnerName,
              videoVersionId: id,
            },
            { event: "video completion" },
          );
        }
      },
    },
  );

  useLogEvent({
    event: "Liability Disclaimer Viewed",
    properties: {},
    options: {
      referrer: () => ({}),
      enabled:
        adViewedSuccessfully &&
        calculatedAdTag?.type === VIDEO_PRE_ROLL_TYPES.DISCLAIMER,
    },
  });

  const onAdStart = () => {
    if (calculatedAdTag.type === VIDEO_PRE_ROLL_TYPES.SLATE) {
      sendImpression({
        impressionType: IMPRESSION_TYPES.PRE_ROLL,
        adProductType: AD_PRODUCTS_TYPES.PRE_ROLL_SLATE,
        adProductPartnerId: adProductPreRollSlate.adProductPartnerId,
        partnerId: adProductPreRollSlate.partnerId,
        partnerName: adProductPreRollSlate.partnerName,
        videoVersionId: id,
      });
    }
  };

  const onVideoDisplayClick = useCallback(() => {
    if (user.isAnonymous) {
      setIsUserSelfIdModalOpen(true);
    }
  }, [user.isAnonymous]);

  const onUserSelfIdModalToggle = useCallback((isOpen) => {
    setIsUserSelfIdModalOpen(isOpen);
  }, []);

  const gptAdState = {
    gptAdPath: config.GPT_AD_PATH,
    gptAdTargetId: config.GPT_AD_TARGET_ID,
  };

  function gptAdReducer(state, action) {
    switch (action.type) {
      case USER_TYPES.UNREGISTERED_FAMILY.id:
        return {
          gptAdPath: config.GPT_AD_PATH_FAMILY,
          gptAdTargetId: config.GPT_AD_TARGET_ID_FAMILY,
        };
      case USER_TYPES.UNREGISTERED_TEACHER.id:
        return {
          gptAdPath: config.GPT_AD_PATH_EDUCATOR,
          gptAdTargetId: config.GPT_AD_TARGET_ID_EDUCATOR,
        };
      default:
        return gptAdState;
    }
  }
  const [adUnitState, dispatchUserTypeChanged] = useReducer(
    gptAdReducer,
    gptAdState,
  );

  useEffect(() => {
    dispatchUserTypeChanged({ type: user.userTypeId });

    const { googletag } = window;

    if (googletag) {
      googletag.cmd.push(() => {
        googletag
          .defineSlot(adUnitState.gptAdPath, [1, 1], adUnitState.gptAdTargetId)
          .addService(googletag.pubads());
        googletag.enableServices();
        googletag.cmd.push(() => {
          googletag.display(adUnitState.gptAdTargetId);
        });
      });
    }
    return () => {
      if (googletag) {
        googletag.cmd.push(() => {
          googletag.destroySlots();
        });
      }
    };
  }, [adUnitState.gptAdTargetId, adUnitState.gptAdPath, user.userTypeId]);

  return (
    <Layout pageTitle={title} pageDescription={description}>
      {/* TEMP AD UNIT TEST START */}
      <div id={adUnitState.gptAdTargetId} />
      {/* TEMP AD UNIT TEST END | This div is being targeted by GPT scripts and displaying a 1px by 1px ad */}
      <div className="container max-w-5xl mx-auto md:pt-sm">
        {/* The Player needs to reload if changes are made to the unsafeOptions prop using this is currently safe because we are using the key prop */}
        <Player
          key={jwPlayerId + user.userTypeId}
          unsafeOptions={{
            autostart: !user.isAnonymous,
            playlist: userTypesEligibleForAutoPlay.includes(user.userTypeId)
              ? process.env.DEPLOY_ENV === "production"
                ? "Sx7rwJe6"
                : "qdA9AhtJ"
              : undefined,
            adTag: calculatedAdTag?.url,
          }}
          onAdComplete={() => adViewed(calculatedAdTag.type)}
          onAdStart={onAdStart}
        >
          <Video
            options={{
              source: jwPlayerId,
              disabled: user.isAnonymous,
            }}
            onDisplayClick={onVideoDisplayClick}
          />

          <VideoSession title={title} />
          <UserSelfIdModal
            open={isUserSelfIdModalOpen}
            onOpenChange={onUserSelfIdModalToggle}
            analyticsProps={{
              contentType: CONTENT_TYPES.VIDEO,
              id: videoId,
              title: title.toLowerCase(),
            }}
          />
        </Player>

        <VideoFooter
          ref={partnerLogoInViewRef}
          videoDetails={{
            title,
            description,
            contentTags,
            printable,
            sponsorIcon: partner?.logo,
            icon: channelIcon,
            partnerUrl: partner?.url,
            partnerName: partner?.name,
          }}
          printableMediaCard={
            <MediaCardWithLink
              id={printable?.id}
              description={printable?.description}
              slug={printable?.slug}
              title={printable?.title}
              images={{ tile: printable?.image }}
              layout="tall"
              contentLinkAsBase={`/${ROUTE_PREFIX.ACTIVITIES}`}
              contentLinkHrefBase={`/${ROUTE_PREFIX.ACTIVITIES}/[...identity]`}
              linkParams={{
                sourcePage: `${title} detail page`,
                sourcePageType: PAGES_TYPES.VIDEO_DETAIL,
                sourceElement: SECTIONS_TYPES.SHELF,
                sourceName: SECTIONS.PAIRED_PRINTABLE,
              }}
            />
          }
          onClick={onTagPress}
          shareContentButton={
            <ShareContentButton
              impressionsProps={{
                contentType: "video",
                id: `${id}`,
                title: `${title}`,
                sourcePage: `${title} detail page`,
                sourcePageType: PAGES_TYPES.VIDEO_DETAIL,
              }}
              linkParams={{
                sourcePage: PAGES_TYPES.SHARE,
                sourcePageType: PAGES_TYPES.SHARE,
                sourceName: SECTIONS_TYPES.SHARE,
                sourceElement: SECTIONS_TYPES.SHARE,
              }}
            />
          }
        />
      </div>

      {recommendedVideos.length ? (
        <VideoRecs>
          {recommendedVideos.slice(0, 4).map((video) => (
            <li key={video.slug}>
              <VideoTile
                duration={video.duration}
                sponsor={{
                  expiration: video.attribution?.expiration,
                  type: video.attribution?.type,
                }}
                title={video.title}
                description={video.description}
                slug={video.slug}
                images={video.images}
                id={video.id}
                linkParams={{
                  sourcePage: `${video.title} detail page`,
                  sourcePageType: PAGES_TYPES.VIDEO_DETAIL,
                  sourceElement: SECTIONS_TYPES.SHELF,
                  sourceName: SECTIONS.YOU_MIGHT_LIKE,
                }}
              />
            </li>
          ))}
        </VideoRecs>
      ) : null}
    </Layout>
  );
};

const VideoSession = ({ title }) => {
  useVideoSession(title);
  return null;
};

export const getServerSideProps = withAuthorization(async (ctx) => {
  const {
    params: { identity },
    query: { sourceId },
  } = ctx;
  const [id] = identity;
  const client = api(ctx);

  const queryClient = new QueryClient();

  await queryClient.prefetchQuery([QUERY_KEYS.VIDEO_DETAILS, id], () =>
    client.getVideoDetails(id),
  );

  await queryClient.prefetchQuery(
    compact([QUERY_KEYS.AD_PRODUCT_PRE_ROLL_SLATE, id, sourceId]),
    () => client.getPreRollSlate(id, sourceId),
  );

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      trackPage: true,
    },
  };
}, ROUTE_PERMISSIONS.PUBLIC);

VideoSession.propTypes = {
  title: PropTypes.string.isRequired,
};

VideoPage.propTypes = {};

export default VideoPage;
